import "@/App.css";
import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import Login from "./pages/Login";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider } from "./lib/auth";

// Lazy-load the heavy studio pages so the first paint isn't blocked
// behind ~600 KB of MockupStudio/BannerStudio/AmpStudio JS that the
// user may not even open. Each chunk loads on first navigation
// (typically already idle-prefetched by the browser by the time the
// user clicks the sidebar).
const Studio = lazy(() => import("./pages/Studio"));
const AmpStudio = lazy(() => import("./pages/AmpStudio"));
const CutoutStudio = lazy(() => import("./pages/CutoutStudio"));
const DimensionCard = lazy(() => import("./pages/DimensionCard"));
const MockupStudio = lazy(() => import("./pages/MockupStudio"));
const BannerStudio = lazy(() => import("./pages/BannerStudio"));
const ProductShoot = lazy(() => import("./pages/ProductShoot"));
const AmazonAplus = lazy(() => import("./pages/AmazonAplus"));
const Gallery = lazy(() => import("./pages/Gallery"));
const NetcoreAnalytics = lazy(() => import("./pages/NetcoreAnalytics"));
const ContentStudio = lazy(() => import("./pages/ContentStudio"));
const RolesAdmin = lazy(() => import("./pages/RolesAdmin"));

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center text-muted-foreground gap-2">
      <Loader2 className="w-4 h-4 animate-spin" />
      Loading workspace…
    </div>
  );
}

function App() {
  return (
    <div className="App grain ambient-blobs">
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Studio />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/amp"
                element={
                  <ProtectedRoute>
                    <AmpStudio />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cutout"
                element={
                  <ProtectedRoute>
                    <CutoutStudio />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dimcard"
                element={
                  <ProtectedRoute>
                    <DimensionCard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mockups"
                element={
                  <ProtectedRoute>
                    <MockupStudio />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/banners"
                element={
                  <ProtectedRoute>
                    <BannerStudio />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/product-shoot"
                element={
                  <ProtectedRoute>
                    <ProductShoot />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aplus"
                element={
                  <ProtectedRoute>
                    <AmazonAplus />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/gallery"
                element={
                  <ProtectedRoute>
                    <Gallery />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics/netcore"
                element={
                  <ProtectedRoute>
                    <NetcoreAnalytics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/content"
                element={
                  <ProtectedRoute>
                    <ContentStudio />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/roles"
                element={
                  <ProtectedRoute>
                    <RolesAdmin />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
      <Toaster position="top-right" richColors closeButton />
    </div>
  );
}

export default App;
